<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import RPGCharactersTemplatePresenter from "@/components/GameMaster/RPGCharacterTempaltes/RPGCharactersTemplatePresenter.vue";
import RPGCharactersTemplatesModal from "@/components/GameMaster/RPGCharacterTempaltes/RPGCharactersTemplatesModal.vue";
import { GMWorldNPCSpawnersApi } from '@/api/backendService.js';
import { worldSpawnersService } from '@/services/WorldSpawnersService';
import type {
  NPCSpawnerCreateRequest,
  GenericSpawner,
  NPCGenericSpawnerRequest,
  PatchedNPCGenericSpawnerRequest,
  NPCSpawnerMinimalPreview
} from '@/api/dx-backend';

interface CharacterTemplate {
  id?: string;
  name: string;
  description?: string;
  behavior?: string;
  avatar?: string;
  category?: string;
  level?: number;
  race?: string;
  class?: string;
}

interface Props {
  positionId: string;
  npcSpawnerId?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  spawnerCreated: [spawnerId: string];
  spawnerUpdated: [spawnerId: string];
  close: [];
}>();

// Form state
const selectedTemplate = ref<CharacterTemplate | null>(null);
const dimensionId = ref<number>(1);
const isActive = ref<boolean>(true);
const spawnLimit = ref<number>(1);
const respawnCycles = ref<number>(1);

// Spawner data state
const currentSpawner = ref<GenericSpawner | null>(null);
const spawnerType = ref<string | null>(null);

// UI state
const isTemplateModalOpen = ref(false);
const isLoading = ref(false);
const error = ref<string | null>(null);
const isSubmitting = ref(false);

// Validation
const isFormValid = computed(() => {
  return selectedTemplate.value && selectedTemplate.value.id;
});

// Template selection handlers
const openTemplateModal = () => {
  isTemplateModalOpen.value = true;
};

const closeTemplateModal = () => {
  isTemplateModalOpen.value = false;
};

const onTemplateSelected = (template: CharacterTemplate) => {
  selectedTemplate.value = template;
  closeTemplateModal();
};

const clearSelectedTemplate = () => {
  selectedTemplate.value = null;
};

// Form submission
const createSpawner = async () => {
  if (!isFormValid.value || !selectedTemplate.value?.id) {
    error.value = 'Please select a character template';
    return;
  }

  isSubmitting.value = true;
  error.value = null;

  try {
    const spawnerData = {
      position: props.positionId,
      character_template: selectedTemplate.value.id,
      dimension: dimensionId.value || undefined,
      is_active: isActive.value,
      spawn_limit: spawnLimit.value,
      respawn_cycles: respawnCycles.value
    };

    const newSpawner = await worldSpawnersService.createNPCSpawner(spawnerData);

    if (newSpawner) {
      emit('spawnerCreated', newSpawner.id || 'unknown');
    }
  } catch (err: any) {
    console.error('Error creating NPC spawner:', err);
    error.value = err.response?.data?.message || err.message || 'Failed to create NPC spawner';
  } finally {
    isSubmitting.value = false;
  }
};

const updateSpawner = async () => {
  if (!isFormValid.value || !selectedTemplate.value?.id || !props.npcSpawnerId) {
    error.value = 'Please select a character template';
    return;
  }

  isSubmitting.value = true;
  error.value = null;

  try {
    const updateData: PatchedNPCGenericSpawnerRequest = {
      character_template: selectedTemplate.value.id,
      dimension: dimensionId.value || undefined,
      is_active: isActive.value,
      spawn_limit: spawnLimit.value,
      respawn_cycles: respawnCycles.value
    };

    const updatedSpawner = await worldSpawnersService.updateNPCSpawner(props.npcSpawnerId, updateData);

    if (updatedSpawner) {
      emit('spawnerUpdated', updatedSpawner.id || 'unknown');
    }
  } catch (err: any) {
    console.error('Error updating NPC spawner:', err);
    error.value = err.response?.data?.message || err.message || 'Failed to update NPC spawner';
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = async () => {
  if (props.npcSpawnerId) {
    await updateSpawner();
  } else {
    await createSpawner();
  }
};

// Load existing spawner data if editing
onMounted(async () => {
  if (props.npcSpawnerId) {
    try {
      isLoading.value = true;
      console.log('Loading existing spawner:', props.npcSpawnerId);

      // Initialize the spawners service if needed
      await worldSpawnersService.initialize();

      // Try to find the spawner (could be generic or NPC)
      const spawner = worldSpawnersService.getSpawnerById(props.npcSpawnerId);

      if (spawner) {
        currentSpawner.value = spawner;

        // Check if it's a generic spawner with object_type
        if ('object_type' in spawner && spawner.object_type) {
          spawnerType.value = spawner.object_type.model;

          // If it's an NPC spawner, extract template from real_instance
          if (spawner.real_instance && typeof spawner.real_instance === 'object') {
            const realInstance = spawner.real_instance as any;
            if (realInstance.character_template) {
              // Set the template ID for prefilling
              selectedTemplate.value = {
                id: realInstance.character_template,
                name: 'Loading...' // Will be updated when template loads
              };
            }
          }
        }

        // If it's an NPCGenericSpawner, get template directly
        if ('character_template' in spawner && spawner.character_template) {
          selectedTemplate.value = {
            id: spawner.character_template,
            name: 'Loading...' // Will be updated when template loads
          };
        }

        // Populate form fields
        if (spawner.dimension !== undefined) {
          dimensionId.value = spawner.dimension || 1;
        }
        isActive.value = spawner.is_active ?? true;
        spawnLimit.value = spawner.spawn_limit || 1;
        respawnCycles.value = spawner.respawn_cycles || 1;

        console.log('Loaded spawner data:', spawner);
        console.log('Spawner type:', spawnerType.value);
      } else {
        console.warn('Spawner not found:', props.npcSpawnerId);
        error.value = 'Spawner not found';
      }
    } catch (err) {
      console.error('Failed to load spawner data:', err);
      error.value = 'Failed to load spawner data';
    } finally {
      isLoading.value = false;
    }
  }
});
</script>

<template>
  <div class="gm-panel gm-panel--raised npc-spawner-constructor">
    <div class="header">
      <div class="header-top">
        <h2 class="gm-title title">
          {{ npcSpawnerId ? 'Edit NPC Spawner' : 'Create NPC Spawner' }}
        </h2>
        <button @click="emit('close')" class="close-btn" title="Close Spawner Constructor">
          ×
        </button>
      </div>
      <!-- Spawner Type Display -->
      <div v-if="spawnerType && npcSpawnerId" class="spawner-type-info">
        <span class="gm-label spawner-type-label">Spawner Type:</span>
        <span class="gm-chip spawner-type-value">{{ spawnerType }}</span>
      </div>
    </div>

    <div class="form-content gm-scroll">
      <!-- Error Display -->
      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <!-- 2-Column Layout: Template Image + Form Fields -->
      <div class="main-content-row">
        <!-- Left Column: Character Template Selection -->
        <div class="template-column">
          <label class="gm-section-title section-label">Character Template *</label>
          <div class="template-selection">
            <div v-if="selectedTemplate" class="selected-template">
              <RPGCharactersTemplatePresenter
                :template-id="selectedTemplate.id"
                :selected="true"
                class="template-display"
                @click="openTemplateModal"
              />
              <button
                @click="clearSelectedTemplate"
                class="clear-template-btn"
                title="Clear selected template"
              >
                ×
              </button>
            </div>
            <div v-else class="template-placeholder" @click="openTemplateModal">
              <div class="placeholder-icon">+</div>
              <div class="placeholder-text">Select Character Template</div>
            </div>
          </div>
        </div>

        <!-- Right Column: Form Fields -->
        <div class="form-column">
          <div class="form-fields">
            <!-- Dimension ID -->
            <div class="gm-field">
              <label for="dimensionId" class="gm-label">Dimension ID</label>
              <input
                id="dimensionId"
                v-model.number="dimensionId"
                type="number"
                min="1"
                max="10"
                class="gm-input"
                placeholder="Dimension ID (1-10)"
              />
            </div>

            <!-- Is Active -->
            <div class="gm-field checkbox-group">
              <label class="checkbox-label">
                <input
                  v-model="isActive"
                  type="checkbox"
                  class="form-checkbox"
                />
                <span class="checkbox-text">Active Spawner</span>
              </label>
            </div>

            <!-- Spawn Limit -->
            <div class="gm-field">
              <label for="spawnLimit" class="gm-label">Spawn Limit</label>
              <input
                id="spawnLimit"
                v-model.number="spawnLimit"
                type="number"
                min="0"
                max="2147483647"
                class="gm-input"
                placeholder="Maximum spawns (0 = unlimited)"
              />
            </div>

            <!-- Respawn Cycles -->
            <div class="gm-field">
              <label for="respawnCycles" class="gm-label">Respawn Cycles</label>
              <input
                id="respawnCycles"
                v-model.number="respawnCycles"
                type="number"
                min="1"
                max="50"
                class="gm-input"
                placeholder="Number of respawn cycles"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="form-actions">
        <button
          @click="emit('close')"
          class="gm-btn gm-btn--ghost"
          :disabled="isSubmitting"
        >
          Cancel
        </button>
        <button
          @click="handleSubmit"
          class="gm-btn gm-btn--primary"
          :disabled="!isFormValid || isSubmitting"
        >
          <span v-if="isSubmitting">
            {{ npcSpawnerId ? 'Updating...' : 'Creating...' }}
          </span>
          <span v-else>
            {{ npcSpawnerId ? 'Update Spawner' : 'Create Spawner' }}
          </span>
        </button>
      </div>
    </div>

    <!-- Template Selection Modal -->
    <div v-if="isTemplateModalOpen" class="modal-overlay" @click="closeTemplateModal">
      <div class="modal-content" @click.stop>
        <RPGCharactersTemplatesModal
          :selectedTemplateId="selectedTemplate?.id"
          @templateSelected="onTemplateSelected"
          @close="closeTemplateModal"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.npc-spawner-constructor {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 500px;
  max-height: 700px;
  width: 48rem;
  max-width: 95vw;
}

.header {
  flex-shrink: 0;
  margin-bottom: var(--gm-gap);
}

.spawner-type-info {
  margin-top: var(--gm-gap-sm);
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
}

.title {
  margin: 0;
  flex: 1;
  text-align: center;
}

.close-btn {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 2rem;
  height: 2rem;
  border: 1px solid var(--gm-border-dim);
  border-radius: 50%;
  background: var(--gm-surface-sunken);
  color: var(--gm-text-muted);
  font-family: var(--gm-font-display);
  font-size: 1.2rem;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s ease, border-color 0.15s ease;
  line-height: 1;
}

.close-btn:hover {
  border-color: var(--gm-border);
  color: var(--gm-accent);
}

.form-content {
  flex: 1;
  padding: 0.5rem;
}

.error-message {
  padding: 0.7rem;
  margin-bottom: var(--gm-gap);
  background: rgba(201, 69, 63, 0.12);
  border: 1px solid rgba(201, 69, 63, 0.4);
  border-radius: var(--gm-radius-sm);
  color: var(--gm-danger);
  text-align: center;
}

/* 2-Column Layout */
.main-content-row {
  display: flex;
  gap: var(--gm-gap-lg);
  margin-bottom: var(--gm-gap-lg);
}

.template-column {
  flex: 0 0 auto;
  min-width: 220px;
}

.form-column {
  flex: 1;
  min-width: 300px;
}

.section-label {
  display: block;
  margin-bottom: var(--gm-gap-sm);
}

.template-selection {
  position: relative;
}

.selected-template {
  position: relative;
  display: inline-block;
  width: 100%;
}

.template-display {
  cursor: pointer;
}

.clear-template-btn {
  position: absolute;
  top: -0.5rem;
  right: -0.5rem;
  width: 1.5rem;
  height: 1.5rem;
  border: 1px solid rgba(201, 69, 63, 0.4);
  border-radius: 50%;
  background: var(--gm-surface-2);
  color: var(--gm-danger);
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s ease, background 0.15s ease;
  line-height: 1;
}

.clear-template-btn:hover {
  border-color: var(--gm-danger);
  background: rgba(201, 69, 63, 0.14);
}

.template-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 140px;
  border: 1px dashed var(--gm-border-dim);
  border-radius: var(--gm-radius);
  background: var(--gm-surface-sunken);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.template-placeholder:hover {
  border-color: var(--gm-border);
  background: var(--gm-accent-soft);
}

.placeholder-icon {
  font-size: 2rem;
  color: var(--gm-text-dim);
  margin-bottom: var(--gm-gap-sm);
  font-weight: bold;
}

.placeholder-text {
  color: var(--gm-text-muted);
  font-size: 0.9rem;
  text-align: center;
}

.form-fields {
  display: grid;
  gap: var(--gm-gap);
  margin-bottom: var(--gm-gap-lg);
}

.checkbox-group {
  flex-direction: row;
  align-items: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: var(--gm-text);
}

.form-checkbox {
  margin-right: var(--gm-gap-sm);
  width: 1.2rem;
  height: 1.2rem;
  accent-color: var(--gm-accent);
}

.checkbox-text {
  font-size: 0.9rem;
  font-weight: 500;
}

.form-actions {
  display: flex;
  gap: var(--gm-gap);
  justify-content: flex-end;
  padding-top: var(--gm-gap);
  border-top: 1px solid var(--gm-border-dim);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  width: 90%;
  max-width: 1000px;
  height: 80%;
  max-height: 700px;
}

/* Responsive design */
@media (max-width: 768px) {
  .main-content-row {
    flex-direction: column;
    gap: var(--gm-gap);
  }

  .template-column {
    min-width: auto;
  }

  .form-column {
    min-width: auto;
  }

  .form-actions {
    flex-direction: column;
  }

  .template-placeholder {
    width: 100%;
  }
}
</style>